import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Compare() {
  // SEO Configuration
  useSEO({
    title: 'Compare Healthcare Providers - The Villarreal Health Network',
    description: 'Compare home healthcare providers in Las Vegas. Detailed comparison of skilled nursing, personal care, therapy services, and specialized medical support from our network agencies.',
    keywords: 'compare healthcare providers, home health agencies comparison, skilled nursing services, personal care comparison, therapy services Las Vegas',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "name": "Compare Healthcare Providers",
          "description": "Detailed comparison of healthcare providers in The Villarreal Health Network",
          "provider": getOrganizationSchema()
        },
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Compare Providers', url: '/compare' }
        ])
      ]
    }
  });

  const [selectedFilter, setSelectedFilter] = useState('all');

  const services = [
    'Skilled Nursing',
    'Primary Care',
    'Psychiatric Services',
    'Pain Management',
    'Wound Care',
    'Physical Therapy',
    'Occupational Therapy',
    'Speech Therapy',
    'Personal Care',
    'Companion Care',
    'Alzheimer\'s Support',
    'Respite Care',
    'Private Pay',
    'Medicaid Waiver',
    'Medicare Certified'
  ];

  const agencies = [
    {
      name: 'Med-Care Providers',
      description: 'Comprehensive outpatient medical center providing physical and mental health care for adults and seniors, including specialized in-home clinical visits',
      badge: 'Full Medical Services',
      color: 'blue',
      services: {
        'Skilled Nursing': false,
        'Primary Care': true,
        'Psychiatric Services': true,
        'Pain Management': true,
        'Wound Care': true,
        'Physical Therapy': false,
        'Occupational Therapy': false,
        'Speech Therapy': false,
        'Personal Care': false,
        'Companion Care': false,
        'Alzheimer\'s Support': false,
        'Respite Care': false,
        'Private Pay': true,
        'Medicaid Waiver': true,
        'Medicare Certified': true
      },
      specialties: ['Medicare/Medicaid Accepted', 'Multilingual Staff', 'Telehealth Services', 'Walk-in Appointments', 'Home Clinical Visits'],
      phone: '(702) 555-1001',
      uniqueFeatures: [
        'Comprehensive routine checkups & chronic disease management',
        'Psychiatric evaluations available onsite & via telehealth',
        'Non-opioid pain management solutions & therapeutic injections',
        'Advanced wound care including wound vacuum therapy',
        'Physician and nurse practitioner home visits for homebound patients'
      ],
      image: 'Modern medical clinic with primary care physician consulting with senior patient, professional healthcare provider in white coat explaining treatment options in bright contemporary medical office, doctor patient consultation in comfortable medical facility'
    },
    {
      name: 'Celebrity Home Health',
      description: 'Licensed skilled home health agency providing clinical recovery and rehabilitation care services in the comfort of your home',
      badge: 'Medicare Certified',
      color: 'teal',
      services: {
        'Skilled Nursing': true,
        'Primary Care': false,
        'Psychiatric Services': false,
        'Pain Management': false,
        'Wound Care': true,
        'Physical Therapy': true,
        'Occupational Therapy': true,
        'Speech Therapy': true,
        'Personal Care': false,
        'Companion Care': false,
        'Alzheimer\'s Support': false,
        'Respite Care': false,
        'Private Pay': true,
        'Medicaid Waiver': false,
        'Medicare Certified': true
      },
      specialties: ['Medicare-Certified Agency', 'State Licensed', 'High Patient Satisfaction', 'Direct Hospital Referrals'],
      phone: '(702) 555-1002',
      uniqueFeatures: [
        'IV therapy and comprehensive medication management',
        'Post-operative rehabilitation and recovery services',
        'Catheter care, ostomy management, and advanced wound care',
        'Swallowing evaluations and speech therapy services',
        'Medical social services and care coordination'
      ],
      image: 'Professional skilled nurse providing IV therapy to patient in comfortable home setting, licensed healthcare provider administering medical treatment in living room, home health nursing care with medical equipment'
    },
    {
      name: 'Accent Senior Care',
      description: 'Non-skilled in-home care agency providing reliable, affordable daily assistance and companionship services for seniors',
      badge: 'Daily Living Support',
      color: 'green',
      services: {
        'Skilled Nursing': false,
        'Primary Care': false,
        'Psychiatric Services': false,
        'Pain Management': false,
        'Wound Care': false,
        'Physical Therapy': false,
        'Occupational Therapy': false,
        'Speech Therapy': false,
        'Personal Care': true,
        'Companion Care': true,
        'Alzheimer\'s Support': false,
        'Respite Care': false,
        'Private Pay': true,
        'Medicaid Waiver': true,
        'Medicare Certified': false
      },
      specialties: ['Medicaid Waiver Accepted', 'Background Checked Staff', 'CNA-Trained Caregivers', 'Backup Caregiver Program'],
      phone: '(702) 555-1003',
      uniqueFeatures: [
        'Light housekeeping and nutritious meal preparation',
        'Culturally appropriate cooking and dietary preferences',
        'Fall prevention assistance and mobility support',
        'Social enrichment activities and companionship',
        'Optional care management coordination with Med-Care'
      ],
      image: 'Caring personal care assistant helping senior with daily activities in bright home kitchen, professional caregiver preparing healthy meal with elderly client, companion care services in comfortable home environment'
    },
    {
      name: 'A Peace of Mind Caregivers',
      description: 'Licensed personal care agency specializing in high-touch, personalized care for seniors and adults with disabilities and memory issues',
      badge: 'Memory Care Experts',
      color: 'purple',
      services: {
        'Skilled Nursing': false,
        'Primary Care': false,
        'Psychiatric Services': false,
        'Pain Management': false,
        'Wound Care': false,
        'Physical Therapy': false,
        'Occupational Therapy': false,
        'Speech Therapy': false,
        'Personal Care': true,
        'Companion Care': true,
        'Alzheimer\'s Support': true,
        'Respite Care': true,
        'Private Pay': true,
        'Medicaid Waiver': true,
        'Medicare Certified': false
      },
      specialties: ['Medicaid Waiver Approved', 'VA Trusted Provider', 'Compassion-First Training', 'Flexible Care Plans'],
      phone: '(702) 555-1004',
      uniqueFeatures: [
        'Cognitive engagement activities and memory stimulation',
        'Specialized Alzheimer\'s and dementia care planning',
        'Transportation services to medical appointments',
        'Daytime and overnight respite care services',
        'Memory support techniques and behavioral redirection'
      ],
      image: 'Specialized dementia caregiver engaging with Alzheimer patient in memory care activities, compassionate care provider working with senior in comfortable home setting, memory care specialist providing cognitive support'
    }
  ];

  const filteredAgencies = selectedFilter === 'all' 
    ? agencies 
    : agencies.filter(agency => agency.services[selectedFilter]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-teal-600 to-blue-700 text-white py-24 pt-36 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/7a2b37a009e13cebf3c9430f1bb21e2c.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg">
                Find Your Perfect Healthcare Match
              </span>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-8">
              Compare Our Healthcare Providers
            </h1>
            <p className="text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              Discover the perfect healthcare solution for your needs. Each agency specializes in different services to provide comprehensive care across Nevada.
            </p>
          </div>
        </div>
      </section>

      {/* Enhanced Filter Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 border-b-2 border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 lg:mb-0">
              Filter by Service Type:
            </h2>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setSelectedFilter('all')}
                className={`px-10 py-5 rounded-full text-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105 shadow-lg ${
                  selectedFilter === 'all'
                    ? 'bg-blue-600 text-white shadow-xl'
                    : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-blue-300'
                }`}
              >
                All Services
              </button>
              {services.map((service) => (
                <button
                  key={service}
                  onClick={() => setSelectedFilter(service)}
                  className={`px-10 py-5 rounded-full text-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105 shadow-lg ${
                    selectedFilter === service
                      ? 'bg-blue-600 text-white shadow-xl'
                      : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-blue-300'
                  }`}
                >
                  {service}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Comparison Table */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Service Comparison Matrix
            </h2>
            <p className="text-xl text-gray-600">
              Compare services across our network agencies
            </p>
          </div>
          
          <div className="overflow-x-auto shadow-2xl rounded-3xl">
            <table className="w-full bg-white overflow-hidden">
              <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                <tr>
                  <th className="px-10 py-8 text-left text-xl font-bold text-gray-900">
                    Services
                  </th>
                  {filteredAgencies.map((agency) => (
                    <th key={agency.name} className="px-10 py-8 text-center text-xl font-bold text-gray-900 min-w-64">
                      <div className="flex flex-col items-center">
                        <span className={`inline-block w-6 h-6 rounded-full bg-${agency.color}-500 mb-4 shadow-lg`}></span>
                        <span className="font-bold text-xl mb-2">{agency.name}</span>
                        <span className={`text-sm bg-${agency.color}-100 text-${agency.color}-800 px-4 py-2 rounded-full font-semibold`}>
                          {agency.badge}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {services.map((service, index) => (
                  <tr key={service} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-10 py-6 text-xl font-semibold text-gray-900">
                      {service}
                    </td>
                    {filteredAgencies.map((agency) => (
                      <td key={`${agency.name}-${service}`} className="px-10 py-6 text-center">
                        {agency.services[service] ? (
                          <div className="flex items-center justify-center">
                            <i className="ri-check-line text-green-600 text-3xl bg-green-100 rounded-full p-2"></i>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <i className="ri-close-line text-gray-400 text-3xl bg-gray-100 rounded-full p-2"></i>
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Enhanced Agency Details */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-20">
            Detailed Agency Profiles
          </h2>
          
          <div className="space-y-16">
            {filteredAgencies.map((agency, index) => (
              <div key={index} className="bg-white rounded-3xl shadow-2xl overflow-hidden hover:shadow-3xl transition-shadow duration-500">
                <div className="lg:flex">
                  <div className="lg:w-1/3">
                    <div 
                      className="h-80 lg:h-full bg-cover bg-center"
                      style={{
                        backgroundImage: `url('https://readdy.ai/api/search-image?query=$%7Bagency.image.replace%28%2Fbrown%7Cdark%7Cdim%2Fgi%2C%20bright%20white%20clean%20modern%20glass%20windows%20natural%20lighting%29%7D&width=600&height=400&seq=agency-bright-${index}-v3&orientation=landscape')`
                      }}
                    />
                  </div>
                  <div className="lg:w-2/3 p-12">
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex items-center">
                        <span className={`inline-block w-8 h-8 rounded-full bg-${agency.color}-500 mr-6 shadow-lg`}></span>
                        <h3 className="text-4xl font-bold text-gray-900">{agency.name}</h3>
                      </div>
                      <span className={`bg-${agency.color}-100 text-${agency.color}-800 text-lg font-bold px-8 py-4 rounded-full whitespace-nowrap`}>
                        {agency.badge}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-10 text-xl leading-relaxed">{agency.description}</p>
                    
                    <div className="mb-10">
                      <h4 className="font-bold text-gray-900 mb-6 text-2xl">Key Features:</h4>
                      <div className="grid grid-cols-1 gap-4">
                        {agency.uniqueFeatures.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-start">
                            <i className="ri-check-line text-green-600 mr-4 mt-1 flex-shrink-0 text-xl bg-green-100 rounded-full p-2"></i>
                            <span className="text-gray-700 text-lg">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mb-10">
                      <h4 className="font-bold text-gray-900 mb-6 text-2xl">Specialties:</h4>
                      <div className="flex flex-wrap gap-4">
                        {agency.specialties.map((specialty, specialtyIndex) => (
                          <span key={specialtyIndex} className="bg-gray-100 text-gray-700 text-lg px-8 py-4 rounded-full font-medium">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-8 border-t-2 border-gray-200">
                      <a href={`tel:${agency.phone}`} className="text-blue-600 font-bold text-xl hover:text-blue-700 transition-colors duration-200">
                        <i className="ri-phone-line mr-3"></i>
                        {agency.phone}
                      </a>
                      <Link to="/referral">
                        <Button size="lg" className="whitespace-nowrap transform hover:scale-105 transition-all duration-200 px-10 py-5 text-lg shadow-lg">
                          <i className="ri-file-text-line mr-3"></i>
                          Start Referral
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Service Categories */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-20">
            Service Categories Explained
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="text-center p-10 bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl hover:shadow-xl transition-shadow duration-300">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-stethoscope-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Medical Services</h3>
              <p className="text-gray-600 text-lg leading-relaxed">Primary care, psychiatric services, pain management, and advanced wound care by licensed medical professionals.</p>
            </div>

            <div className="text-center p-10 bg-gradient-to-br from-teal-50 to-teal-100 rounded-3xl hover:shadow-xl transition-shadow duration-300">
              <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-nurse-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Skilled Nursing</h3>
              <p className="text-gray-600 text-lg leading-relaxed">Licensed nurses providing IV therapy, medication management, wound care, and clinical recovery services.</p>
            </div>

            <div className="text-center p-10 bg-gradient-to-br from-green-50 to-green-100 rounded-3xl hover:shadow-xl transition-shadow duration-300">
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-heart-pulse-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Therapy Services</h3>
              <p className="text-gray-600 text-lg leading-relaxed">Physical, occupational, and speech therapy to help patients regain independence and improve quality of life.</p>
            </div>

            <div className="text-center p-10 bg-gradient-to-br from-purple-50 to-purple-100 rounded-3xl hover:shadow-xl transition-shadow duration-300">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-user-heart-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Personal Care</h3>
              <p className="text-gray-600 text-lg leading-relaxed">Daily living assistance, companion care, and specialized support for Alzheimer's and dementia patients.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-teal-600 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-8">
            Ready to Get Started?
          </h2>
          <p className="text-2xl text-blue-100 mb-12 max-w-3xl mx-auto">
            Our intake coordinators will help match you with the right provider based on your specific needs and insurance coverage.
          </p>
          <div className="flex flex-col sm:flex-row gap-8 justify-center">
            <Link to="/referral">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto whitespace-nowrap text-xl px-10 py-4">
                <i className="ri-file-text-line mr-3"></i>
                Submit a Referral
              </Button>
            </Link>
            <Button variant="outline" size="lg" className="w-full sm:w-auto bg-white/10 border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap cursor-pointer text-xl px-10 py-4"
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}>
              <i className="ri-customer-service-line mr-3"></i>
              Talk to Care Coordinator
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
